/** Orchestrator Bootstrap stage rich rendering.
 *
 * This is the FINAL agent — the senior-legal-analyst writes the
 * client-facing report. The HF prompt produces a 9-section structured
 * write-up. We render each section as its own card, and fall back to the
 * full narrative if structured fields are missing.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText } from 'lucide-react';

const asText = (v: unknown): string => (typeof v === 'string' ? v : '');

const SECTIONS: { key: string; label: string }[] = [
  { key: 'executive_summary', label: '1. Executive summary' },
  { key: 'contract_overview', label: '2. Contract overview' },
  { key: 'key_findings', label: '3. Key findings' },
  { key: 'compliance_status', label: '4. Compliance status' },
  { key: 'execution_status', label: '5. Execution status' },
  { key: 'clause_analysis_summary', label: '6. Clause analysis summary' },
  { key: 'missing_provisions', label: '7. Missing provisions' },
  { key: 'recommendations', label: '8. Recommendations' },
  { key: 'pipeline_quality_note', label: '9. Pipeline quality note' },
];

export function RichBootstrapBody({ output }: { output: Record<string, unknown> }) {
  const hasStructured = SECTIONS.some((s) => asText(output[s.key]).trim().length > 0);
  const narrative = asText(output.narrative);

  if (!hasStructured && narrative) {
    // The agent returned only prose, not the structured sections. Render
    // the narrative as a single readable card. The reviewer can still see
    // the raw output under the JSON details below.
    return (
      <div className="rounded border bg-muted/30 p-4">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium">Final client report</span>
        </div>
        <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">
          {narrative}
        </pre>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {SECTIONS.map((s) => {
        const body = asText(output[s.key]);
        if (!body.trim()) return null;
        return (
          <Card key={s.key} className="border-border">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm font-medium">{s.label}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3 px-4">
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{body}</p>
            </CardContent>
          </Card>
        );
      })}
      {!hasStructured && (
        <p className="text-xs text-muted-foreground italic">
          The agent did not return structured sections for this run.
        </p>
      )}
    </div>
  );
}
